import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PhoneInputWithSearch from '../components/PhoneInputWithSearch';
import { ChevronLeft, Check, Upload, FileText, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
    { id: 'safari', label: 'Safari Tours' },
    { id: 'mountain', label: 'Mountain Treks' },
    { id: 'beach', label: 'Beach Holidays' },
    { id: 'cultural', label: 'Cultural Tours' },
    { id: 'wildlife', label: 'Wildlife Photography' },
    { id: 'adventure', label: 'Adventure Sports' }
];

const locations = [
    { id: 'serengeti', label: 'Serengeti' },
    { id: 'kilimanjaro', label: 'Kilimanjaro' },
    { id: 'zanzibar', label: 'Zanzibar' },
    { id: 'ngorongoro', label: 'Ngorongoro' },
    { id: 'tarangire', label: 'Tarangire' },
    { id: 'selous', label: 'Selous' },
    { id: 'manyara', label: 'Lake Manyara' },
    { id: 'ruaha', label: 'Ruaha' }
];

export default function OperatorRegistration() {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Step 1: Account details
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');

    // Step 2: Company info
    const [companyName, setCompanyName] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [description, setDescription] = useState('');

    // Step 3: Services & locations
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);

    // Step 4: Document uploads
    const [documents, setDocuments] = useState({});
    const [docNames, setDocNames] = useState({});
    const [uploading, setUploading] = useState(null);
    const [selectedDocType, setSelectedDocType] = useState('');

    const DOC_TYPES = [
        { key: 'business_license', label: 'Business License', required: true },
        { key: 'tala', label: 'TALA Certificate' },
        { key: 'brela', label: 'BRELA Certificate' },
        { key: 'nida', label: 'NIDA (Director)' },
    ];

    const { registerOperator } = useAuth();
    const navigate = useNavigate();

    // Persist form data in sessionStorage to survive navigation
    useEffect(() => {
        const saved = sessionStorage.getItem('operatorRegForm');
        if (saved) {
            try {
                const s = JSON.parse(saved);
                if (s.phone) setPhone(s.phone);
                if (s.name) setName(s.name);
                if (s.companyName) setCompanyName(s.companyName);
                if (s.licenseNumber) setLicenseNumber(s.licenseNumber);
                if (s.description) setDescription(s.description);
                if (s.selectedServices) setSelectedServices(s.selectedServices);
                if (s.selectedLocations) setSelectedLocations(s.selectedLocations);
                if (s.documents) setDocuments(s.documents);
                if (s.docNames) setDocNames(s.docNames);
                if (s.step) setStep(s.step);
            } catch {}
        }
    }, []);

    useEffect(() => {
        const data = { phone, name, companyName, licenseNumber, description, selectedServices, selectedLocations, documents, docNames, selectedDocType, step };
        sessionStorage.setItem('operatorRegForm', JSON.stringify(data));
    }, [phone, name, companyName, licenseNumber, description, selectedServices, selectedLocations, documents, docNames, selectedDocType, step]);

    const toggleService = (id) => {
        setSelectedServices(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const toggleLocation = (id) => {
        setSelectedLocations(prev =>
            prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
        );
    };

    const validateStep1 = () => {
        if (!phone) { toast.error('Please enter your phone number'); return false; }
        if (!name.trim()) { toast.error('Please enter your name'); return false; }
        if (!pin || pin.length < 4) { toast.error('PIN must be at least 4 digits'); return false; }
        if (pin !== confirmPin) { toast.error('PINs do not match'); return false; }
        return true;
    };

    const validateStep2 = () => {
        if (!companyName.trim()) { toast.error('Please enter your company name'); return false; }
        if (!description.trim()) { toast.error('Please describe your company'); return false; }
        return true;
    };

    const handleSubmit = async () => {
        if (!documents.business_license) {
            toast.error('Business License is mandatory');
            return;
        }

        setLoading(true);
        try {
            await registerOperator({
                phone,
                name,
                pin,
                company_name: companyName,
                license_number: licenseNumber,
                description,
                services: selectedServices,
                locations: selectedLocations,
                documents: Object.fromEntries(Object.entries(documents).filter(([, v]) => v))
            });
            toast.success('Registration submitted! Awaiting admin approval.');
            sessionStorage.removeItem('operatorRegForm');
            navigate('/operator');
        } catch (error) {
            console.error('Registration error:', error.response?.status, error.response?.data, error.message);
            toast.error(error.response?.data?.detail || error.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const uploadDocument = async (file, docType) => {
        if (!file) return;
        setUploading(docType);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_URL}/api/upload/document`, { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setDocuments(prev => ({ ...prev, [docType]: data.url }));
            setDocNames(prev => ({ ...prev, [docType]: file.name }));
            setSelectedDocType('');
            toast.success(`${DOC_TYPES.find(d => d.key === docType)?.label || docType} uploaded`);
        } catch {
            toast.error('Upload failed. Please try again.');
        } finally {
            setUploading(null);
        }
    };

    const nextStep = () => {
        if (step === 1 && !validateStep1()) return;
        if (step === 2 && !validateStep2()) return;
        if (step === 3) {
            if (selectedServices.length === 0) { toast.error('Please select at least one service'); return; }
            if (selectedLocations.length === 0) { toast.error('Please select at least one location'); return; }
        }
        if (step === 4) { handleSubmit(); return; }
        setStep(s => s + 1);
    };

    const prevStep = () => setStep(s => Math.max(1, s - 1));

    return (
        <div className="min-h-screen bg-white flex">
            {/* Left panel - image + branding */}
            <div
                className="hidden lg:block lg:w-5/12 relative"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=1200)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-[#073763]/80 flex flex-col justify-between p-12">
                    <Link to="/" className="text-2xl font-bold text-white" data-testid="op-reg-brand">
                        safarinodes
                    </Link>

                    <div>
                        <h2 className="text-3xl lg:text-4xl text-white font-bold mb-4 leading-tight">
                            Partner With<br />Tanzania's #1 Travel Platform
                        </h2>
                        <p className="text-white/80 max-w-sm mb-8">
                            Join our network of verified operators and connect with travelers from around the world.
                        </p>

                        <div className="space-y-4">
                            {[
                                'Receive trip requests matching your expertise',
                                'Submit personalized itineraries',
                                'Grow your business with global exposure'
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                        <Check size={14} className="text-white" />
                                    </div>
                                    <span className="text-white/90 text-sm">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className="text-white/40 text-sm">
                        Already registered? <Link to="/auth" className="text-white underline">Sign in here</Link>
                    </p>
                </div>
            </div>

            {/* Right panel - multi-step form */}
            <div className="w-full lg:w-7/12 flex items-start justify-center p-6 md:p-12 overflow-y-auto">
                <div className="w-full max-w-lg py-4">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-[#073763] mb-6 transition-colors"
                        data-testid="op-reg-back"
                    >
                        <ChevronLeft size={18} />
                        Back to Home
                    </Link>

                    {/* Mobile brand - visible on small screens */}
                    <h1 className="text-2xl font-bold text-[#073763] mb-2 lg:hidden">safarinodes</h1>

                    <h2 className="text-xl font-bold text-[#1A1A1A] mb-1" data-testid="op-reg-title">
                        Tour Operator Registration
                    </h2>
                    <p className="text-gray-500 text-sm mb-8">
                        Complete the form below to join our platform. Accounts require admin approval.
                    </p>

                    {/* Progress steps */}
                    <div className="flex items-center gap-2 mb-8" data-testid="op-reg-progress">
                        {[
                            { num: 1, label: 'Account' },
                            { num: 2, label: 'Company' },
                            { num: 3, label: 'Services' },
                            { num: 4, label: 'Documents' }
                        ].map((s, idx) => (
                            <div key={s.num} className="flex items-center flex-1">
                                <div className="flex items-center gap-2 flex-1">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 transition-colors ${
                                        step > s.num ? 'bg-[#073763] text-white' :
                                        step === s.num ? 'bg-[#073763] text-white' :
                                        'bg-gray-200 text-gray-500'
                                    }`}>
                                        {step > s.num ? <Check size={16} /> : s.num}
                                    </div>
                                    <span className={`text-xs font-medium hidden sm:block ${
                                        step >= s.num ? 'text-[#073763]' : 'text-gray-400'
                                    }`}>{s.label}</span>
                                </div>
                                {idx < 3 && (
                                    <div className={`h-px flex-1 mx-2 ${
                                        step > s.num ? 'bg-[#073763]' : 'bg-gray-200'
                                    }`} />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Step 1: Account Details */}
                    {step === 1 && (
                        <div className="space-y-5 animate-fade-in" data-testid="op-reg-step-1">
                            <div>
                                <Label className="form-label">Full Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Your full name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="input-karibu"
                                    required
                                    data-testid="op-reg-name"
                                />
                            </div>

                            <div>
                                <Label className="form-label">Phone Number</Label>
                                <PhoneInputWithSearch
                                    value={phone}
                                    onChange={setPhone}
                                    defaultCountry="TZ"
                                    data-testid="op-reg-phone"
                                />
                            </div>

                            <div>
                                <Label className="form-label">Create PIN (4-6 digits)</Label>
                                <Input
                                    type="password"
                                    placeholder="Create a secure PIN"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    className="input-karibu"
                                    maxLength={6}
                                    required
                                    data-testid="op-reg-pin"
                                />
                            </div>

                            <div>
                                <Label className="form-label">Confirm PIN</Label>
                                <Input
                                    type="password"
                                    placeholder="Re-enter your PIN"
                                    value={confirmPin}
                                    onChange={(e) => setConfirmPin(e.target.value)}
                                    className="input-karibu"
                                    maxLength={6}
                                    required
                                    data-testid="op-reg-confirm-pin"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Company Info */}
                    {step === 2 && (
                        <div className="space-y-5 animate-fade-in" data-testid="op-reg-step-2">
                            <div>
                                <Label className="form-label">Company Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Your company name"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="input-karibu"
                                    required
                                    data-testid="op-reg-company"
                                />
                            </div>

                            <div>
                                <Label className="form-label">Tourism License Number (Optional)</Label>
                                <Input
                                    type="text"
                                    placeholder="e.g. TALA-2024-XXXX"
                                    value={licenseNumber}
                                    onChange={(e) => setLicenseNumber(e.target.value)}
                                    className="input-karibu"
                                    data-testid="op-reg-license"
                                />
                            </div>

                            <div>
                                <Label className="form-label">Company Description</Label>
                                <textarea
                                    placeholder="Tell travelers about your company, years of experience, specializations..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="input-karibu w-full min-h-[120px] resize-none"
                                    required
                                    data-testid="op-reg-description"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 3: Services & Locations */}
                    {step === 3 && (
                        <div className="space-y-6 animate-fade-in" data-testid="op-reg-step-3">
                            <div>
                                <Label className="form-label mb-3">Services You Offer</Label>
                                <p className="text-xs text-gray-400 mb-3">Select all that apply</p>
                                <div className="flex flex-wrap gap-2">
                                    {services.map(service => (
                                        <button
                                            key={service.id}
                                            type="button"
                                            onClick={() => toggleService(service.id)}
                                            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${
                                                selectedServices.includes(service.id)
                                                    ? 'bg-[#073763] text-white border-[#073763]'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#073763]/40'
                                            }`}
                                            data-testid={`op-reg-service-${service.id}`}
                                        >
                                            {selectedServices.includes(service.id) && <Check size={14} className="inline mr-1.5 -mt-0.5" />}
                                            {service.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label className="form-label mb-3">Operating Locations</Label>
                                <p className="text-xs text-gray-400 mb-3">Select all regions you operate in</p>
                                <div className="flex flex-wrap gap-2">
                                    {locations.map(location => (
                                        <button
                                            key={location.id}
                                            type="button"
                                            onClick={() => toggleLocation(location.id)}
                                            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${
                                                selectedLocations.includes(location.id)
                                                    ? 'bg-[#073763] text-white border-[#073763]'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#073763]/40'
                                            }`}
                                            data-testid={`op-reg-location-${location.id}`}
                                        >
                                            {selectedLocations.includes(location.id) && <Check size={14} className="inline mr-1.5 -mt-0.5" />}
                                            {location.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Document Uploads */}
                    {step === 4 && (
                        <div className="space-y-5 animate-fade-in" data-testid="op-reg-step-4">
                            <p className="text-sm text-gray-500">Upload your registration documents. <span className="font-semibold text-gray-700">Business License</span> is mandatory.</p>

                            {/* Uploaded documents list */}
                            {Object.keys(documents).length > 0 && (
                                <div className="space-y-2" data-testid="uploaded-docs-list">
                                    {Object.entries(documents).map(([key, url]) => {
                                        const docType = DOC_TYPES.find(d => d.key === key);
                                        return (
                                            <div key={key} className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid={`uploaded-doc-${key}`}>
                                                <FileText size={18} className="text-emerald-600 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-emerald-800">{docType?.label || key}</p>
                                                    <p className="text-xs text-emerald-600 truncate">{docNames[key] || 'Uploaded'}</p>
                                                </div>
                                                <Check size={16} className="text-emerald-600 shrink-0" />
                                                <button onClick={() => {
                                                    const updated = { ...documents }; delete updated[key];
                                                    const updatedNames = { ...docNames }; delete updatedNames[key];
                                                    setDocuments(updated); setDocNames(updatedNames);
                                                }} className="p-1.5 hover:bg-emerald-100 rounded-lg text-emerald-500 hover:text-red-500 transition" data-testid={`remove-doc-${key}`}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Dropdown + upload area for next document */}
                            {Object.keys(documents).length < DOC_TYPES.length && (
                                <div className="border border-gray-200 rounded-xl p-5" data-testid="doc-upload-area">
                                    <Label className="form-label mb-2">Select document type</Label>
                                    <select
                                        value={selectedDocType}
                                        onChange={e => setSelectedDocType(e.target.value)}
                                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white mb-4"
                                        data-testid="doc-type-select"
                                    >
                                        <option value="">Choose a document...</option>
                                        {DOC_TYPES.filter(d => !documents[d.key]).map(d => (
                                            <option key={d.key} value={d.key}>
                                                {d.label}{d.required ? ' *' : ''}
                                            </option>
                                        ))}
                                    </select>

                                    {selectedDocType && (
                                        <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 cursor-pointer transition ${
                                            uploading ? 'border-[#073763] bg-[#073763]/5' : 'border-gray-200 hover:border-[#073763]/40 hover:bg-gray-50'
                                        }`} data-testid="doc-upload-label">
                                            {uploading ? (
                                                <Loader2 size={24} className="text-[#073763] animate-spin" />
                                            ) : (
                                                <Upload size={24} className="text-gray-400" />
                                            )}
                                            <span className="text-sm text-gray-500 font-medium">
                                                {uploading ? 'Uploading...' : `Upload ${DOC_TYPES.find(d => d.key === selectedDocType)?.label}`}
                                            </span>
                                            <span className="text-xs text-gray-400">PDF, JPG, PNG accepted</span>
                                            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                                                onChange={e => e.target.files[0] && uploadDocument(e.target.files[0], selectedDocType)}
                                                disabled={uploading !== null}
                                                data-testid="doc-file-input" />
                                        </label>
                                    )}
                                </div>
                            )}

                            {/* Status summary */}
                            <div className="flex items-center gap-2 text-xs text-gray-400 pt-2">
                                <span>{Object.keys(documents).length} of {DOC_TYPES.length} documents uploaded</span>
                                {!documents.business_license && <span className="text-red-500 font-medium">&middot; Business License required</span>}
                            </div>
                        </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
                        {step > 1 ? (
                            <Button
                                type="button"
                                onClick={prevStep}
                                className="bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 rounded-full px-6 py-3"
                                data-testid="op-reg-back-btn"
                            >
                                Back
                            </Button>
                        ) : (
                            <div />
                        )}

                        <Button
                            type="button"
                            onClick={nextStep}
                            className="btn-primary px-8 py-3"
                            disabled={loading || uploading !== null}
                            data-testid="op-reg-next-btn"
                        >
                            {loading ? 'Submitting...' : step === 4 ? 'Submit for Approval' : 'Continue'}
                        </Button>
                    </div>

                    <p className="text-xs text-center text-gray-400 mt-6">
                        Already have an account?{' '}
                        <Link to="/auth" className="text-[#073763] hover:underline font-medium">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
